import { faClipboard } from "@fortawesome/free-regular-svg-icons/faClipboard";
import { faPenToSquare } from "@fortawesome/free-regular-svg-icons/faPenToSquare";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons/faEllipsis";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faFileAlt } from "@fortawesome/free-solid-svg-icons/faFileAlt";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faShare } from "@fortawesome/free-solid-svg-icons/faShare";
import { faTags } from "@fortawesome/free-solid-svg-icons/faTags";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { Switch, Tooltip } from "../../../utils/local-antd";
import type { MenuProps } from "../../../utils/local-antd";

import { TFunction } from "i18next";
import { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import i18n from "../../../i18n";
import { Form as FormModel } from "../../../model/form/Form";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { TestIDs } from "../../../tests/types/testIds";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Spacing } from "../../../theme/Spacing";
import { canCreateForm } from "../utils/FormUtils";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { Status } from "../../../model/shared/enum/Status.enum";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { standardDate } from "../../../utils/Date";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";
import { thresholdSliceSelectors } from "../../../redux/threshold/threshold.selector";
import { thresholdActions } from "../../../redux/threshold/threshold.slice";
import { FormType } from "../../../model/form/types/FormType.enum";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { faCube } from "@fortawesome/free-solid-svg-icons/faCube";
import { useAtiraFormContext } from "../context/form-context";
import { useNavigate } from "react-router-dom";
import { useDrawersContext } from "../../tasks/drawers-context";
import { faFolderPlus } from "@fortawesome/free-solid-svg-icons/faFolderPlus";
import { faFolderMinus } from "@fortawesome/free-solid-svg-icons/faFolderMinus";
import { getFontAwesomeIcon } from "../../../utils/getFontAwesomeIcon";
import { formSliceSelectors } from "../../../redux/forms/form.selector";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import isEmpty from "lodash/isEmpty";
import { faFolder } from "@fortawesome/free-solid-svg-icons/faFolder";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import { faUsersGear } from "@fortawesome/free-solid-svg-icons/faUsersGear";
import { EntityIcons } from "../../../utils/GlobalIcons";
import { faTimeline } from "@fortawesome/free-solid-svg-icons/faTimeline";
import isNumber from "lodash/isNumber";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { isCustomDateInput } from "../../../utils/isCustomDateInput";

const Wrapper = styled(Flex)<{ $highlighted: boolean }>`
  flex-direction: column;
  box-shadow: ${({ $highlighted, theme }) =>
    $highlighted ? `0 0 0 0.18rem ${theme.darkMain}, ${Shadow.MD}` : Shadow.SM};
  scroll-margin-top: 8rem;
  border-radius: ${Rounded.md};
  transition: box-shadow 0.35s ease-out;
  width: 100%;

  @keyframes border-pulsate {
    0% {
      box-shadow:
        0 0 0 0.18rem ${({ theme }) => theme.darkMain},
        ${Shadow.MD};
    }

    55% {
      box-shadow:
        0 0 0 0.1rem ${({ theme }) => theme.lightMain},
        ${Shadow.SM};
    }

    100% {
      box-shadow: ${Shadow.SM};
    }
  }

  animation: ${({ $highlighted }) =>
    $highlighted ? "border-pulsate 3s ease-out 4 forwards" : "none"};
`;

const ActionsWrapper = styled(Flex)`
  display: none;
  gap: ${Spacing.l};
  transition: all 0.3s;
  align-items: center;
  opacity: 0;

  @media (min-width: 1200px) {
    display: flex;
  }
`;

const FormContainer = styled(Flex)`
  padding: 0 ${Spacing.m};
  background-color: ${(props) => props.theme.white};
  border-radius: ${Rounded.md} ${Rounded.md} 0 0;
  position: relative;

  &:hover {
    ${ActionsWrapper} {
      opacity: 1;
    }
  }
`;

const DetailsContainer = styled(Flex)`
  background-color: ${({ theme }) => theme.lightMain};
  border-radius: 0 0 ${Rounded.md} ${Rounded.md};
  align-items: center;
  gap: ${Spacing.s};
  overflow: hidden;
  padding-inline-start: ${Spacing.m};
`;

const StatusButton = styled(Button)<{ enabled: boolean }>`
  padding: 0;
  margin: 0;
  background-color: transparent;
  color: ${({ theme, enabled }) => theme[enabled ? "success" : "danger"]};
  position: absolute;
  top: calc(${Spacing.s} / 2);
  html[dir="rtl"] & {
    right: calc(${Spacing.s} / 2);
  }
  html[dir="ltr"] & {
    left: calc(${Spacing.s} / 2);
  }
`;

const StyledButton = styled(Button)`
  background-color: ${(props) => props.theme.transparent};
  padding: ${Spacing.xs};
`;

const ActionContainer = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
`;

const StatusContainer = styled(Flex)<{ active: boolean }>`
  padding: ${Spacing.xs} ${Spacing.s};
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  background-color: ${({ theme }) => theme.lightSub};
  color: ${({ theme, active }) => theme[active ? "success" : "danger"]};
`;

type FNType = ATVoidFunction<FormModel>;

type GetMenuItemsArgs = {
  t: TFunction;
  currentForm: FormModel | null;
  functions: {
    onDelete: FNType;
    onShare: FNType;
    onPreview: FNType;
    // onNavigateToEntries: FNType;
    onEdit: FNType;
    onDuplicate: FNType | null;
    onCreateEntry?: FNType;
    onAssign?: FNType;
    onAutoAssign?: FNType;
    onConfigureReCaptcha?: FNType;
    onUpdateStatus?: FNType;
    onAddToFolder?: FNType;
    onRemoveFromFolder?: FNType;
    onViewActivities?: FNType;
    onCalendar?: FNType;
  };
  hasFolders?: boolean;
  /* Passed from the component so the desktop/compact split re-evaluates on
     resize and rotation; falls back to a one-off read when omitted. */
  viewportWidth?: number;
};

const getDesktopMenuOptions = ({
  t,
  currentForm,
  functions,
}: GetMenuItemsArgs) =>
  [
    functions.onAssign
      ? {
          label: (
            <ActionContainer>
              <AtiraIcon icon={faPenToSquare} color="main" />
              <Text color="main">{t("common.assign")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onAssign!(currentForm!),
        }
      : null,
    functions.onUpdateStatus
      ? {
          label: (
            <ActionContainer>
              <AtiraIcon icon={faCheck} color="main" />
              <Text color="main">
                {t(
                  currentForm?.status === Status.ACTIVE
                    ? "forms.actions.status.active.title"
                    : "forms.actions.status.inactive.title",
                )}
              </Text>
            </ActionContainer>
          ),
          onClick: () => functions.onUpdateStatus!(currentForm!),
        }
      : null,
    {
      label: (
        <ActionContainer
          dataTestId={TestIDs.forms.form.actions.delete(currentForm!._id)}
        >
          <AtiraIcon icon={faTrash} color="main" />
          <Text color="main">{t("common.delete")}</Text>
        </ActionContainer>
      ),
      onClick: () => functions.onDelete(currentForm!),
    },
  ]
    .filter(Boolean)
    .map((item, index) => ({ ...item, key: `${index}` }));

const getTabletMenuItems = ({ t, currentForm, functions }: GetMenuItemsArgs) =>
  [
    functions.onAssign
      ? {
          label: (
            <ActionContainer>
              <AtiraIcon icon={faPenToSquare} color="main" />
              <Text color="main">{t("common.assign")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onAssign!(currentForm!),
        }
      : null,
    functions.onUpdateStatus
      ? {
          label: (
            <ActionContainer>
              <AtiraIcon icon={faCheck} color="main" />
              <Text color="main">
                {t(
                  currentForm?.status === Status.ACTIVE
                    ? "forms.actions.status.active.title"
                    : "forms.actions.status.inactive.title",
                )}
              </Text>
            </ActionContainer>
          ),
          onClick: () => functions.onUpdateStatus!(currentForm!),
        }
      : null,
    currentForm?.status !== Status.INACTIVE
      ? {
          label: (
            <ActionContainer>
              <AtiraIcon icon={faShare} color="main" />
              <Text color="main">{t("common.share")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onShare(currentForm!),
        }
      : null,
    {
      label: (
        <ActionContainer>
          <AtiraIcon icon={faEye} color="main" />
          <Text color="main">{t("common.preview")}</Text>
        </ActionContainer>
      ),
      onClick: () => functions.onPreview(currentForm!),
    },
    // {
    //   label: (
    //     <ActionContainer>
    //       <AtiraIcon icon={faTags} color="main" />
    //       <Text color="main">{t("common.entries")}</Text>
    //     </ActionContainer>
    //   ),
    //   onClick: () => functions.onNavigateToEntries(currentForm!),
    // },
    ...(functions.onCreateEntry
      ? [
          {
            label: (
              <ActionContainer>
                <AtiraIcon icon={faPlus} color="main" />
                <Text color="main">{t("forms.form.new_entry")}</Text>
              </ActionContainer>
            ),
            onClick: () => functions.onCreateEntry!(currentForm!),
          },
        ]
      : []),
    {
      label: (
        <ActionContainer>
          <AtiraIcon icon={faEdit} color="main" />
          <Text color="main">{t("common.edit")}</Text>
        </ActionContainer>
      ),
      onClick: () => functions.onEdit(currentForm!),
    },
    {
      label: (
        <ActionContainer>
          <AtiraIcon icon={faTrash} color="main" />
          <Text color="main">{t("common.delete")}</Text>
        </ActionContainer>
      ),
      onClick: () => functions.onDelete(currentForm!),
    },
  ]
    .filter(Boolean)
    .map((item, index) => ({ ...item, key: `${index}` }));

const getOtherMenuItems = ({
  t,
  currentForm,
  functions,
  hasFolders,
}: GetMenuItemsArgs) => [
  ...(functions.onDuplicate
    ? [
        {
          key: "duplicate-form",
          label: (
            <ActionContainer
              dataTestId={TestIDs.forms.form.actions.clone(currentForm!._id)}
            >
              <AtiraIcon icon={faClipboard} color="main" />
              <Text color="main">{t("common.duplicate")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onDuplicate!(currentForm!),
        },
      ]
    : []),
  ...(functions.onConfigureReCaptcha
    ? [
        {
          key: "configure-reCaptcha",
          label: (
            <ActionContainer>
              <AtiraIcon icon={faShieldHalved} color="main" />
              <Text color="main">{t("forms.actions.reCaptchaConfigure")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onConfigureReCaptcha!(currentForm!),
        },
      ]
    : []),
  ...(functions.onAutoAssign
    ? [
        {
          key: "auto-assign",
          label: (
            <ActionContainer>
              <AtiraIcon icon={faUsersGear} color="main" />
              <Text color="main">{t("forms.actions.autoAssign")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onAutoAssign!(currentForm!),
        },
      ]
    : []),
  ...(functions.onAddToFolder && hasFolders
    ? [
        {
          key: "add-to-folder",
          label: (
            <ActionContainer
              dataTestId={TestIDs.forms.form.actions.addToFolder(
                currentForm!._id,
              )}
            >
              <AtiraIcon icon={faFolderPlus} color="main" />
              <Text color="main">{t("forms.actions.addToFolder")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onAddToFolder!(currentForm!),
        },
      ]
    : []),
  ...(functions.onRemoveFromFolder
    ? [
        {
          key: "remove-from-folder",
          label: (
            <ActionContainer
              dataTestId={TestIDs.forms.form.actions.removeFromFolder(
                currentForm!._id,
              )}
            >
              <AtiraIcon icon={faFolderMinus} color="main" />
              <Text color="main">{t("forms.actions.removeFromFolder")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onRemoveFromFolder!(currentForm!),
        },
      ]
    : []),
  ...(functions.onViewActivities
    ? [
        {
          key: "view-activities",
          label: (
            <ActionContainer>
              <AtiraIcon icon={faTimeline} color="main" />
              <Text color="main">{t("common.activities")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onViewActivities!(currentForm!),
        },
      ]
    : []),
  ...(functions.onCalendar
    ? [
        {
          key: "calendar",
          label: (
            <ActionContainer
              dataTestId={TestIDs.forms.form.actions.calendar(currentForm!._id)}
            >
              <AtiraIcon icon={faCalendar} color="main" />
              <Text color="main">{t("forms.actions.calendar")}</Text>
            </ActionContainer>
          ),
          onClick: () => functions.onCalendar!(currentForm!),
        },
      ]
    : []),
];

const getMenuItems = ({
  t,
  currentForm,
  functions,
  hasFolders,
  viewportWidth,
}: GetMenuItemsArgs): MenuProps["items"] => [
  {
    key: "g1",
    type: "group",
    children: ((viewportWidth ?? document.body.clientWidth) > 1200
      ? getDesktopMenuOptions({
          t,
          currentForm,
          functions,
        })
      : getTabletMenuItems({
          t,
          currentForm,
          functions,
        })
    ).concat(
      getOtherMenuItems({
        t,
        currentForm,
        functions,
        hasFolders,
      }),
    ),
  },
];

type FormProps = {
  form: FormModel;
  highlighted?: boolean;
};

export const Form: React.FC<FormProps> = ({ form, highlighted = false }) => {
  const { t } = useTranslation();
  const { width: viewportWidth } = useWindowDimensions();
  const { setEntryCreateVisible, setSubToUnlock } = useDrawersContext();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const {
    setFormCreateEditDrawerVisible,
    setSelectedForm,
    setSelectedFolder,

    setFormShareVisible,
    setFormReadVisible,
    setFormDeleteVisible,
    setFormDuplicateVisible,
    setFormUpdateStatusVisible,
    setFormAssignVisible,
    setFormReCaptchaConfigureVisible,
    setFormAutoAssignVisible,
    setFormAddToFolderVisible,
    setFormRemoveFromFolderVisible,
    setFormActivityVisible,
    setFormCalendarVisible,
  } = useAtiraFormContext();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const hasFolders = Boolean(
    useAppSelector(formSliceSelectors.selectFolders)?.length,
  );
  const parentFolder = useAppSelector((s) =>
    formSliceSelectors.selectFolderByFormId(s, form._id || ""),
  );
  const thresholds = useAppSelector(
    thresholdSliceSelectors.selectThresholdStats,
  );
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const parentModuleDefinition =
    useAppSelector((s) =>
      moduleSliceSelectors.selectModuleDefinitionById(s, form?.parentId || ""),
    ) || form?.parent;
  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);

  const onEdit = useCallback(() => {
    setSelectedForm(form);
    setFormCreateEditDrawerVisible(true);
  }, [form, setFormCreateEditDrawerVisible, setSelectedForm]);

  const onShare = useCallback(() => {
    setSelectedForm(form);
    setFormShareVisible(true);
  }, [form, setSelectedForm, setFormShareVisible]);

  const onPreview = useCallback(() => {
    setSelectedForm(form);
    setFormReadVisible(true);
  }, [form, setSelectedForm, setFormReadVisible]);

  const onNavigateToEntries = useCallback(() => {
    setSelectedForm(form);
    if (form.type === FormType.MODULE) {
      navigate(`/modules/${form.parentId}`);
    } else {
      navigate("/entries", { state: { useFilter: { form } } });
    }
  }, [form, navigate, setSelectedForm]);

  const onCreateEntry = useCallback(() => {
    setSelectedForm(form);
    setEntryCreateVisible(true);
  }, [form, setSelectedForm, setEntryCreateVisible]);

  const onDelete = useCallback(() => {
    setSelectedForm(form);
    setFormDeleteVisible(true);
  }, [form, setFormDeleteVisible, setSelectedForm]);

  const onDuplicate = useCallback(async () => {
    // The forms page fetches thresholds on mount without waiting, so a quick
    // click can land before they arrive - and canCreateForm reads "not
    // loaded" as "limit reached", showing the upgrade modal with no limit
    let current = thresholds;
    if (!current) {
      try {
        current = await dispatch(
          thresholdActions.getThresholds({ userId: user._id }),
        ).unwrap();
      } catch {
        current = null;
      }
    }

    if (!canCreateForm(current)) {
      setSubToUnlock({
        visible: true,
        title: t("subToUnlock.specific.formsCount", {
          limit: current?.total?.FORMS,
        }),
      });
      return;
    }

    setSelectedForm(form);
    setFormDuplicateVisible(true);
  }, [
    form,
    setSelectedForm,
    setFormDuplicateVisible,
    thresholds,
    dispatch,
    user._id,
    t,
    setSubToUnlock,
  ]);

  const onViewActivities = useCallback(() => {
    setSelectedForm(form);
    setFormActivityVisible(true);
  }, [form, setFormActivityVisible, setSelectedForm]);

  const onCalendar = useCallback(() => {
    setSelectedForm(form);
    setFormCalendarVisible(true);
  }, [form, setFormCalendarVisible, setSelectedForm]);

  const onAssign = useCallback(() => {
    setSelectedForm(form);
    setFormAssignVisible(true);
  }, [form, setSelectedForm, setFormAssignVisible]);

  const onConfigureReCaptcha = useCallback(() => {
    setSelectedForm(form);
    setFormReCaptchaConfigureVisible(true);
  }, [form, setFormReCaptchaConfigureVisible, setSelectedForm]);

  const onAutoAssign = useCallback(() => {
    setSelectedForm(form);
    setFormAutoAssignVisible(true);
  }, [form, setSelectedForm, setFormAutoAssignVisible]);

  const onUpdateStatus = () => {
    setSelectedForm(form);
    setFormUpdateStatusVisible(true);
  };

  const onAddToFolder = useCallback(() => {
    setSelectedForm(form);
    setFormAddToFolderVisible(true);
  }, [form, setSelectedForm, setFormAddToFolderVisible]);

  const onRemoveFromFolder = useCallback(() => {
    if (isEmpty(parentFolder)) {
      return;
    }

    setSelectedForm(form);
    setSelectedFolder(parentFolder);
    setFormRemoveFromFolderVisible(true);
  }, [
    form,
    parentFolder,
    setSelectedFolder,
    setSelectedForm,
    setFormRemoveFromFolderVisible,
  ]);

  const parentModuleIcon = parentModuleDefinition
    ? getFontAwesomeIcon(parentModuleDefinition.icon) || faCube
    : faCube;
  const userPermissions = user?.permissions.permissions;
  const isOrganization = members?.length > 0;
  const canUpdate = userPermissions?.FORM.UPDATE;
  const canShare = form?.status === Status.ACTIVE;
  const hasBookableDateInput = Boolean(
    form.inputs?.some((input) =>
      isCustomDateInput(input.type, input.dateInputConfig),
    ),
  );
  const assignees = members?.filter((member) =>
    form.assigneeIds?.includes(member._id),
  );

  useEffect(() => {
    if (!highlighted) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      wrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [highlighted]);

  const items = useMemo(
    () =>
      getMenuItems({
        t,
        currentForm: form,
        functions: {
          onDelete,
          onPreview,
          onShare,
          // onNavigateToEntries,
          onCreateEntry:
            userPermissions.ENTRY.CREATE && form.type === FormType.ENTRY
              ? onCreateEntry
              : undefined,
          onEdit,
          onDuplicate: onDuplicate,
          onAssign: isOrganization ? onAssign : undefined,
          onConfigureReCaptcha: !isOrgMember ? onConfigureReCaptcha : undefined,
          onAutoAssign: isOrganization ? onAutoAssign : undefined,
          onAddToFolder:
            canUpdate && isEmpty(parentFolder) ? onAddToFolder : undefined,
          onRemoveFromFolder:
            canUpdate && !isEmpty(parentFolder)
              ? onRemoveFromFolder
              : undefined,
          onViewActivities: !isOrgMember ? onViewActivities : undefined,
          onCalendar:
            form.type === FormType.ENTRY && hasBookableDateInput
              ? onCalendar
              : undefined,
        },
        hasFolders,
        viewportWidth,
      }),
    [
      t,
      form,
      parentFolder,
      hasFolders,
      viewportWidth,
      onDelete,
      onPreview,
      onShare,
      onNavigateToEntries,
      onCreateEntry,
      onEdit,
      onDuplicate,
      onAssign,
      onConfigureReCaptcha,
      onAutoAssign,
      onAddToFolder,
      onRemoveFromFolder,
      onViewActivities,
      onCalendar,
      hasBookableDateInput,
      canUpdate,
      isOrganization,
      isOrgMember,
      userPermissions.ENTRY.CREATE,
    ],
  );

  return (
    <Wrapper
      ref={wrapperRef}
      $highlighted={highlighted}
      dataTestId={TestIDs.forms.forms.wrapper(form._id)}
    >
      <FormContainer width="100%" alignItems="center">
        <Tooltip
          title={t(
            form.status === Status.ACTIVE ? "common.active" : "common.inactive",
          )}
        >
          <StatusButton
            enabled={form.status === Status.ACTIVE}
            icon={faCircle}
            iconWidth="xs"
          />
        </Tooltip>
        <Flex justifyContent="space-between" width="100%" alignItems="center">
          <Flex gap="m" alignItems="center">
            <AtiraIcon icon={faFileAlt} size="3x" color="main" />

            <Text
              fontSize="l"
              color={form?.status !== Status.ACTIVE ? "gray" : "black"}
            >
              {form.title}
            </Text>

            {form.type === FormType.ENTRY ? (
              <Flex justifyContent="center" alignItems="center" gap="m">
                <AtiraIcon
                  icon={faCircle}
                  size="2xs"
                  color={form?.status !== Status.ACTIVE ? "gray" : "main"}
                />

                {isNumber(form.entries) ? (
                  <Text fontSize="l" color="gray">
                    {form.entries} {t("common.entry")}
                  </Text>
                ) : null}
              </Flex>
            ) : null}

            <AssigneeBanner assignees={assignees} />
          </Flex>

          <Flex alignItems="center" gap="m">
            <ActionsWrapper>
              {/* {userPermissions?.ENTRY.READ ? (
                <Tooltip title={t("common.entries")}>
                  <StyledButton
                    iconWidth="2x"
                    onClick={onNavigateToEntries}
                    icon={faTags}
                    color="main"
                  />
                </Tooltip>
              ) : null} */}

              {userPermissions?.ENTRY.CREATE && form.type === FormType.ENTRY ? (
                <Tooltip title={t("forms.form.new_entry")}>
                  <StyledButton
                    iconWidth="2x"
                    onClick={onCreateEntry}
                    icon={faPlus}
                    color="main"
                  />
                </Tooltip>
              ) : null}

              <Tooltip title={t("common.preview")}>
                <StyledButton
                  iconWidth="2x"
                  onClick={onPreview}
                  icon={faEye}
                  color="main"
                />
              </Tooltip>

              {canShare ? (
                <Tooltip title={t("common.share")}>
                  <StyledButton
                    iconWidth="2x"
                    icon={faShare}
                    onClick={onShare}
                    color="main"
                  />
                </Tooltip>
              ) : null}

              {canUpdate ? (
                <Tooltip title={t("common.edit")}>
                  <StyledButton
                    iconWidth="2x"
                    icon={faEdit}
                    onClick={onEdit}
                    color="main"
                    dataTestId={TestIDs.forms.createEdit.edit.button(form._id)}
                  />
                </Tooltip>
              ) : null}
            </ActionsWrapper>

            {userPermissions?.FORM.DELETE ? (
              <AtiraDropdown
                items={items}
                placement={
                  i18n.language === AppLangs.AR ? "bottomLeft" : "bottomRight"
                }
              >
                <Tooltip title={t("common.actions")}>
                  <Button
                    icon={faEllipsis}
                    color="black"
                    iconWidth="2x"
                    backgroundColor="transparent"
                    dataTestId={TestIDs.forms.form.actions.menu(form._id)}
                  />
                </Tooltip>
              </AtiraDropdown>
            ) : null}
          </Flex>
        </Flex>
      </FormContainer>

      <DetailsContainer>
        <Flex flex={1} gap="m">
          {!isEmpty(parentFolder) ? (
            <Tooltip
              title={t("form.details.folder", {
                name: parentFolder.name,
              })}
            >
              <Flex alignItems="center" gap="xs">
                <AtiraIcon icon={faFolder} color="sub" />
                <TextWithTruncated
                  length={Lengths.NAME}
                  fontSize="s"
                  color="sub"
                >
                  {parentFolder.name}
                </TextWithTruncated>
              </Flex>
            </Tooltip>
          ) : null}

          <Tooltip
            title={t("form.details.owner", {
              owner: form?.owner?.name || user.name,
            })}
          >
            <Flex alignItems="center" gap="xs">
              <AtiraIcon icon={faUser} color="sub" />
              <TextWithTruncated length={Lengths.NAME} fontSize="s" color="sub">
                {form?.owner?.name || user.name}
              </TextWithTruncated>
            </Flex>
          </Tooltip>

          {members
            .filter((m) => form.assigneeIds?.includes(m._id))
            .map((assignee) => (
              <Tooltip
                key={assignee?._id}
                title={t("form.assignee", {
                  name: assignee?.name || user.name,
                })}
              >
                <Flex alignItems="center" gap="xs">
                  <AtiraIcon icon={faUserPlus} color="sub" />
                  <Text fontSize="s" color="sub">
                    {assignee?.name || user.name}
                  </Text>
                </Flex>
              </Tooltip>
            ))}

          {form.type === FormType.MODULE ? (
            <Flex alignItems="center" justifyContent="center" gap="xs">
              <AtiraIcon icon={parentModuleIcon} color="sub" />
              <Text fontSize="s" color="sub">
                {form.parent?.name}
              </Text>
            </Flex>
          ) : null}

          <Tooltip
            title={`${t("common.createdAt")}: ${standardDate(form.createdAt)}`}
          >
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faCalendar} color="sub" />
              <Text fontSize="s" color="sub">
                {standardDate<string>(form.createdAt)}
              </Text>
            </Flex>
          </Tooltip>
        </Flex>

        <StatusContainer active={form?.status === Status.ACTIVE}>
          {canUpdate ? (
            <Tooltip
              title={t(
                `form.status.switch.${form?.status === Status.ACTIVE ? "deactivate" : "activate"}.tooltip`,
              )}
            >
              <Switch
                checked={form?.status === Status.ACTIVE}
                onChange={onUpdateStatus}
                checkedChildren={t("common.active")}
                unCheckedChildren={t("common.inactive")}
              />
            </Tooltip>
          ) : (
            <Fragment>
              <AtiraIcon icon={faCircle} size="2xs" />
              {t(
                `common.${form?.status === Status.ACTIVE ? "active" : "inactive"}`,
              )}
            </Fragment>
          )}

          <Flex
            justifyContent="center"
            alignItems="center"
            gap="xs"
            width={"8rem"}
          >
            <Text fontSize="s" color="main">
              {t("common.type")}:{" "}
              {t(`form.status.type.${form?.type?.toLowerCase()}`)}
            </Text>
            <AtiraIcon
              icon={
                form.type === FormType.ENTRY
                  ? EntityIcons.Entries
                  : parentModuleIcon
              }
              color="main"
            />
          </Flex>
        </StatusContainer>
      </DetailsContainer>
    </Wrapper>
  );
};
